import { useContext, useState, useEffect, useRef } from 'react'
import { FontIcon, Stack, TextField } from '@fluentui/react'
import { SendRegular } from '@fluentui/react-icons'
import { SlideMicrophoneRegular } from '@fluentui/react-icons'
import { useSpeechRecognition } from "../Hook/useSpeechRecognition" // 引入 Hook

import Send from '../../assets/Send.svg'
import styles from './QuestionInput.module.css'
import { ChatMessage } from '../../api'
import { AppStateContext } from '../../state/AppProvider'
import { resizeImage } from '../../utils/resizeImage'
import { localizedQuickPromptGroups, t } from '../../i18n'

interface Props {
  onSend: (question: ChatMessage['content'], id?: string) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
  showQuickPrompts?: boolean
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, conversationId, showQuickPrompts = false }: Props) => {
  const [question, setQuestion] = useState<string>('')
  const [base64Image, setBase64Image] = useState<string | null>(null)
  const speechBaseQuestionRef = useRef('')

  const appStateContext = useContext(AppStateContext)
  const OYD_ENABLED = appStateContext?.state.frontendSettings?.oyd_enabled || false
  const language = appStateContext?.state.language || 'zh-TW'
  const quickPromptGroups = localizedQuickPromptGroups(language)


  // 使用自訂的語音識別 hook
  const websocketProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const websocketURL = `${websocketProtocol}//${window.location.host}/ws`
  const { isRecognizing, isPreparing, liveTranscript, error, startRecognition, stopRecognition } = useSpeechRecognition(websocketURL)
    const handleMicrophoneClick = () => {
      if (isRecognizing || isPreparing) {
        stopRecognition();
      } else {
        speechBaseQuestionRef.current = question
        startRecognition().catch((err) => {
          console.error("Error starting recognition:", err);
        });
      }
    };

  const [isLoading, setIsLoading] = useState<boolean>(false); // 加載狀態


  // Replace the current speech draft on each Azure partial; keep the user's text prefix.
  useEffect(() => {
    if (!liveTranscript) return
    const baseQuestion = speechBaseQuestionRef.current
    const separator = baseQuestion && !/\s$/.test(baseQuestion) ? ' ' : ''
    setQuestion(`${baseQuestion}${separator}${liveTranscript}`)
  }, [liveTranscript])

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || "")
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      await convertToBase64(file)
    }
  }

  const convertToBase64 = async (file: Blob) => {
    try {
      const resizedBase64 = await resizeImage(file, 800, 800)
      setBase64Image(resizedBase64)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    const questionTest: ChatMessage["content"] = base64Image ? [{ type: "text", text: question }, { type: "image_url", image_url: { url: base64Image } }] : question.toString()

    if (conversationId && questionTest !== undefined) {
      onSend(questionTest, conversationId)
      setBase64Image(null)
    } else {
      onSend(questionTest)
      setBase64Image(null)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const sendQuickPrompt = (prompt: string) => {
    if (disabled) {
      return
    }

    if (conversationId) {
      onSend(prompt, conversationId)
    } else {
      onSend(prompt)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const sendQuestionDisabled = disabled || !question.trim()

  return (
    <div className={`${styles.questionInputContainer} ${showQuickPrompts ? styles.questionInputWithPrompts : ''}`}>
      <Stack horizontal className={styles.questionInputRow}>
        <TextField
          className={styles.questionInputTextArea}
          placeholder={placeholder}
          multiline
          resizable={false}
          borderless
          value={question}
          onChange={onQuestionChange}
          onKeyDown={onEnterPress}
        />
        {!OYD_ENABLED && (
          <div className={styles.fileInputContainer}>
            <input
              type="file"
              id="fileInput"
              onChange={(event) => handleImageUpload(event)}
              accept="image/*"
              className={styles.fileInput}
            />
            <label htmlFor="fileInput" className={styles.fileLabel} aria-label={t(language, 'uploadImage')}>
              <FontIcon
                className={styles.fileIcon}
                iconName={'PhotoCollection'}
                aria-label={t(language, 'uploadImage')}
              />
            </label>
          </div>)}
        {base64Image && <img className={styles.uploadedImage} src={base64Image} alt={t(language, 'uploadImage')} />}

        <div
          className={styles.microphoneButtonContainer}
          role="button"
          tabIndex={0}
          aria-label={t(language, 'microphone')}
          onClick={handleMicrophoneClick}
        >
           {isPreparing ? (
              <FontIcon iconName="Sync" className={styles.microphoneIconPreparing} aria-hidden="true" />
            ) : isRecognizing ? (
              <SlideMicrophoneRegular className={styles.microphoneIconActive} aria-hidden="true" />
            ) : (
              <SlideMicrophoneRegular className={styles.microphoneIcon} aria-hidden="true" />
            )}
        </div>

        {isLoading && (
          <div className={styles.loadingIndicator}>
            <FontIcon iconName="Loading" aria-label={t(language, 'processing')} className={styles.loadingIcon} />
          </div>
        )}

        <div
          className={styles.questionInputSendButtonContainer}
          role="button"
          tabIndex={0}
          aria-label={t(language, 'askQuestion')}
          onClick={sendQuestion}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
          {sendQuestionDisabled ? (
            <SendRegular className={styles.questionInputSendButtonDisabled} />
          ) : (
            <img src={Send} className={styles.questionInputSendButton} alt="Send Button" />
          )}
        </div>
      </Stack>

      {error && <div className={styles.speechResult} role="alert">{error}</div>}

      {showQuickPrompts && (
        <div className={styles.quickPrompts} aria-label={t(language, 'quickPrompts')}>
          {quickPromptGroups.map(group => (
            <section className={styles.quickPromptGroup} key={group.title} aria-labelledby={`quick-prompt-${group.title}`}>
              <h3 id={`quick-prompt-${group.title}`} className={styles.quickPromptGroupTitle}>{group.title}</h3>
              <div className={styles.quickPromptButtons}>
                {group.prompts.map(prompt => (
                  <button
                    type="button"
                    className={styles.quickPromptButton}
                    key={prompt}
                    disabled={disabled}
                    onClick={() => sendQuickPrompt(prompt)}>
                    {prompt}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      <div className={styles.questionInputBottomBorder} />
    </div>
  )
}
